import { getTodayKey, toDateKeyTz } from '@/lib/tz';
import type { ReviewItem } from '@/types';
import type { Json } from '@repo/supabase-client';

export interface ChildRef {
  id: string;
  name: string;
  emoji_avatar: string | null;
  emoji_color?: string | null;
  avatar_image_path?: string | null;
}

/**
 * Member (household user) reference as returned by the embeds on
 *   tasks.select('*, task_members(user_id, users(id, display_name, ...))')
 */
export interface MemberRef {
  id: string;
  display_name: string | null;
  email: string | null;
  avatar_emoji?: string | null;
  avatar_color?: string | null;
  avatar_image_path?: string | null;
}

export interface MemberJoinRow {
  user_id: string;
  users: MemberRef | null;
}

/**
 * Multi-child join row as returned by Supabase for the shape
 *   tasks.select('*, task_children(child_id, children(id, name, ...))')
 */
export interface ChildJoinRow {
  child_id: string;
  children: ChildRef | null;
}

/**
 * Shape of the `source_review_item` join carried by Today/Schedule
 * rows. Null when the row has no `source_review_item_id`; the inner
 * `email` is null when the email row was purged after ingestion.
 */
export interface SourceEmailJoin {
  email: { subject: string | null } | null;
  /**
   * Whether the source email stated an hour at all. Lives on `review_items`
   * and is NOT copied onto `events` at commit time, so this join is the only
   * way a committed row can still tell "noon" from "we don't know" — the
   * stored start of a date-only extraction is a local-noon day-boundary
   * guard, not a reading anyone wrote (YAR-376 / YAR-378).
   */
  is_time_unknown?: boolean | null;
}

export interface TaskRow {
  id: string;
  title: string;
  due_date: string | null;
  end_date: string | null;
  status: string;
  notes: string | null;
  location: string | null;
  link: string | null;
  links?: { title: string | null; url: string }[] | null;
  /** Set by the DB trigger when the task enters DONE/CANCELLED. Optional because
   *  generated DB types/old literals may omit it; the History query returns it via `*`. */
  completed_at?: string | null;
  /** Legacy scalar kept in sync with the first child in task_children. */
  child_id: string | null;
  /** Real FK set during emails-approve commit (migration 20260421000001). */
  related_event_id: string | null;
  task_children?: ChildJoinRow[] | null;
  task_members?: MemberJoinRow[] | null;
  source_review_item?: SourceEmailJoin | null;
}

export interface EventRow {
  id: string;
  title: string;
  notes: string | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  link: string | null;
  links?: { title: string | null; url: string }[] | null;
  child_id: string | null;
  /**
   * Recurring events (YAR-443): occurrences materialised from one rule share
   * a `series_id`; `recurrence` is a display copy of that rule. Optional on
   * this hand-written row shape so fixtures without them still type-check;
   * the reads return them via `*`.
   */
  series_id?: string | null;
  recurrence?: Json | null;
  event_children?: ChildJoinRow[] | null;
  event_members?: MemberJoinRow[] | null;
  source_review_item?: SourceEmailJoin | null;
}

/** Convenience accessor: returns the source email subject or undefined. */
export function getSourceSubject(
  row: Pick<TaskRow | EventRow, 'source_review_item'>,
): string | undefined {
  return row.source_review_item?.email?.subject ?? undefined;
}

/**
 * Unwraps the join row into a plain array of child refs. Returns [] when
 * the task/event has no assignments.
 */
export function getChildrenFromTask(task: TaskRow): ChildRef[] {
  return (task.task_children ?? [])
    .map((row) => row.children)
    .filter((c): c is ChildRef => c !== null);
}

export function getChildrenFromEvent(event: EventRow): ChildRef[] {
  return (event.event_children ?? [])
    .map((row) => row.children)
    .filter((c): c is ChildRef => c !== null);
}

export function getMembersFromTask(task: TaskRow): MemberRef[] {
  return (task.task_members ?? [])
    .map((row) => row.users)
    .filter((m): m is MemberRef => m !== null);
}

export function getMembersFromEvent(event: EventRow): MemberRef[] {
  return (event.event_members ?? [])
    .map((row) => row.users)
    .filter((m): m is MemberRef => m !== null);
}

function matchesChild(
  joined: ChildJoinRow[] | null | undefined,
  legacyId: string | null,
  selectedChildId: string,
): boolean {
  if (joined && joined.length > 0) {
    return joined.some((row) => row.child_id === selectedChildId);
  }
  return legacyId === selectedChildId;
}

export function taskHasChild(task: TaskRow, childId: string): boolean {
  return matchesChild(task.task_children, task.child_id, childId);
}

export function eventHasChild(event: EventRow, childId: string): boolean {
  return matchesChild(event.event_children, event.child_id, childId);
}

export type FeedItem =
  | { type: 'event'; data: EventRow; sortTime: number }
  | { type: 'task'; data: TaskRow; sortTime: number };

export interface DayGroup {
  dateKey: string;
  date: Date;
  isToday: boolean;
  isPast: boolean;
  items: FeedItem[];
}

function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Group events + tasks under each Date in `days`, returning a DayGroup
 * per input day in the original order. Works for any contiguous (or
 * non-contiguous) Date[] — the original 7-day-week assumption was a
 * shorthand; the infinite-scroll feed passes an N-day window built by
 * `useInfiniteSchedule`. Filters by `selectedChildId` when provided,
 * mirroring `buildDatesWithItems` semantics.
 *
 * Performance: pre-indexed by dateKey so the per-day lookup is O(1).
 * The naive impl was O(N×M) — for each of the N visible days we ran a
 * full filter across all M tasks + events. With ~50 visible days and
 * hundreds of items, that re-ran on every render touching the deps.
 * Pre-pass walks the source rows once, applying the child filter and
 * timezone bucketing along the way, then the per-day walk is a single
 * Map.get(dateKey).
 */
export function buildMultiDayFeed(
  events: EventRow[],
  tasks: TaskRow[],
  days: Date[],
  selectedChildId?: string | null,
  userTimezone?: string | null,
  // Callers holding a live midnight-refreshing key (useTodayKey) pass it so
  // past/today re-buckets on rollover; defaults to a one-shot clock read.
  todayKey: string = getTodayKey(userTimezone),
): DayGroup[] {
  // Pass 1: bucket events + tasks by their dateKey (timezone-aware).
  // The child filter is applied here once per row instead of N times.
  const eventsByDay = new Map<string, EventRow[]>();
  const tasksByDay = new Map<string, TaskRow[]>();

  for (const e of events) {
    if (!e.start_date) continue;
    if (
      selectedChildId &&
      !matchesChild(e.event_children, e.child_id, selectedChildId)
    ) {
      continue;
    }
    const key = toDateKeyTz(e.start_date, userTimezone);
    // Multi-day events are not day cards (YAR-419 / YAR-420). They surface
    // once in the "All ongoing" section and as a span bar pinned under the
    // week strip, for their whole run. Bucketing them here is what made a
    // ten-day window render as a single card on its start day and vanish for
    // the other nine.
    if (e.end_date && toDateKeyTz(e.end_date, userTimezone) !== key) continue;
    const bucket = eventsByDay.get(key);
    if (bucket) bucket.push(e);
    else eventsByDay.set(key, [e]);
  }

  for (const t of tasks) {
    if (!t.due_date) continue;
    if (
      selectedChildId &&
      !matchesChild(t.task_children, t.child_id, selectedChildId)
    ) {
      continue;
    }
    const key = toDateKeyTz(t.due_date, userTimezone);
    const bucket = tasksByDay.get(key);
    if (bucket) bucket.push(t);
    else tasksByDay.set(key, [t]);
  }

  // Pass 2: render every day in `days`. Callers control window size:
  // DateStrip + prev/next arrows are how the user navigates between
  // weeks, and the day list below should match the rendered window.
  // Previously this filtered to `dateKey >= todayKey`, which silently
  // dropped past days — so when "today" was Thu-Sat, the user saw only
  // 1–3 day cards even though the strip showed a full week. TodayTab
  // uses dedicated today-only endpoints; this feed is for the full
  // schedule view.
  return days.map((day) => {
    const dateKey = toLocalDateKey(day);
    const dayEvents = eventsByDay.get(dateKey) ?? [];
    const dayTasks = tasksByDay.get(dateKey) ?? [];
    return {
      dateKey,
      date: day,
      isToday: dateKey === todayKey,
      isPast: dateKey < todayKey,
      items: buildUnifiedFeed(dayEvents, dayTasks),
    };
  });
}

/**
 * Build the set of YYYY-MM-DD keys that have at least one task or event
 * in the user's timezone. Used by DateStrip and MiniCalendar to render
 * the "this day has items" dot. Filters by selectedChildId when provided,
 * matching the same filter semantics as buildMultiDayFeed.
 */
export function buildDatesWithItems(
  tasks: TaskRow[],
  events: EventRow[],
  userTimezone?: string | null,
  selectedChildId?: string | null,
): Set<string> {
  const dateSet = new Set<string>();

  for (const t of tasks) {
    if (!t.due_date) continue;
    if (
      selectedChildId &&
      !matchesChild(t.task_children, t.child_id, selectedChildId)
    ) {
      continue;
    }
    dateSet.add(toDateKeyTz(t.due_date, userTimezone));
  }

  for (const e of events) {
    if (!e.start_date) continue;
    if (
      selectedChildId &&
      !matchesChild(e.event_children, e.child_id, selectedChildId)
    ) {
      continue;
    }
    const startKey = toDateKeyTz(e.start_date, userTimezone);
    dateSet.add(startKey);
    // A multi-day event lights its END day too, but nothing in between
    // (YAR-420). Filling the whole run would paint a fortnight-long window
    // across the strip and bury every other day that needs attention; the two
    // endpoints are the days a parent actually has to do something.
    if (e.end_date) {
      const endKey = toDateKeyTz(e.end_date, userTimezone);
      if (endKey && endKey !== startKey) dateSet.add(endKey);
    }
  }

  return dateSet;
}

/**
 * Split the Today tab's task pool into the three buckets the page
 * renders as separate sections: overdue (open, due before today),
 * todayDated (open, due today), and noDate (open, no due_date — surfaced
 * in a dedicated "Unscheduled" section so they don't drown the
 * time-anchored feed). Future-dated rows are dropped; the service query
 * never returns them but the in-memory filter is defense-in-depth.
 *
 * Every bucket is OPEN tasks only. Happening Today used to keep tasks
 * completed today "so the user sees what they finished", which made it
 * the one section where checking an item left it on the list with the
 * counter unchanged (YAR-399, Sep 2 standup): all three sections now
 * behave the same — a checked task leaves the list and the count drops.
 * The drawer badge sums these buckets through `countTodayItems`, so it
 * follows without a change of its own.
 */
export function partitionTodayTasks(
  tasks: TaskRow[],
  today: string,
  userTimezone?: string | null,
): { overdue: TaskRow[]; todayDated: TaskRow[]; noDate: TaskRow[] } {
  const overdue: TaskRow[] = [];
  const todayDated: TaskRow[] = [];
  const noDate: TaskRow[] = [];

  for (const t of tasks) {
    if (t.status === 'DONE') continue;
    if (!t.due_date) {
      noDate.push(t);
      continue;
    }
    const key = toDateKeyTz(t.due_date, userTimezone);
    if (key === today) {
      todayDated.push(t);
    } else if (key < today) {
      overdue.push(t);
    }
    // Future-dated: dropped.
  }

  return { overdue, todayDated, noDate };
}

export function buildUnifiedFeed(
  events: EventRow[],
  tasks: TaskRow[],
): FeedItem[] {
  const items: FeedItem[] = [
    ...events.map((e) => ({
      type: 'event' as const,
      data: e,
      sortTime: new Date(e.start_date).getTime(),
    })),
    ...tasks.map((t) => ({
      type: 'task' as const,
      data: t,
      sortTime: t.due_date ? new Date(t.due_date).getTime() : Infinity,
    })),
  ];
  items.sort((a, b) => {
    if (a.sortTime !== b.sortTime) return a.sortTime - b.sortTime;
    if (a.type !== b.type) return a.type === 'event' ? -1 : 1;
    return 0;
  });
  return items;
}

const HISTORY_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const HISTORY_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * "Nov 18 · Yesterday · Tuesday" / "Nov 16 · Sunday". `dateKey` and
 * `todayKey` are YYYY-MM-DD (already timezone-resolved by the caller).
 */
export function formatHistoryDateHeader(
  dateKey: string,
  todayKey: string,
): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);

  const [ty, tm, td] = todayKey.split('-').map(Number);
  const yesterday = new Date(ty, tm - 1, td - 1);
  const yesterdayKey = `${yesterday.getFullYear()}-${String(
    yesterday.getMonth() + 1,
  ).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

  const parts = [`${HISTORY_MONTHS[m - 1]} ${d}`];
  if (dateKey === todayKey) parts.push('Today');
  else if (dateKey === yesterdayKey) parts.push('Yesterday');
  parts.push(HISTORY_WEEKDAYS[date.getDay()]);
  return parts.join(' · ');
}

export interface HistoryGroup {
  dateKey: string;
  items: FeedItem[];
}

/**
 * Group completed tasks (by `completed_at`) and past events (by
 * `start_date`) into date buckets, newest date first, items within a day
 * sorted most-recent-first.
 */
export function buildHistoryGroups(
  tasks: TaskRow[],
  events: EventRow[],
  userTimezone?: string | null,
): HistoryGroup[] {
  const byDate = new Map<string, FeedItem[]>();
  const add = (key: string, item: FeedItem) => {
    if (!key) return;
    const bucket = byDate.get(key);
    if (bucket) bucket.push(item);
    else byDate.set(key, [item]);
  };

  for (const e of events) {
    if (!e.start_date) continue;
    // A multi-day event files under the day it ENDED (YAR-176 AC 4). Filing it
    // under the start put a week-long event into History a week before it was
    // over, and then buried it under a heading the parent had already
    // scrolled past. Single-day rows are unaffected: their end is their start.
    const filingDate = e.end_date ?? e.start_date;
    add(toDateKeyTz(filingDate, userTimezone), {
      type: 'event',
      data: e,
      sortTime: new Date(filingDate).getTime(),
    });
  }
  for (const t of tasks) {
    if (!t.completed_at) continue;
    add(toDateKeyTz(t.completed_at, userTimezone), {
      type: 'task',
      data: t,
      sortTime: new Date(t.completed_at).getTime(),
    });
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([dateKey, items]) => ({
      dateKey,
      items: items.sort((x, y) => y.sortTime - x.sortTime),
    }));
}

// ── ItemFormModal adapters ──────────────────────────────────────────

/**
 * Adapt a committed `TaskRow` into the `ReviewItem` shape `ItemFormModal`
 * takes as `initialData`.
 *
 * Four surfaces open that editor over an already-committed row — Today,
 * Schedule, History and Activity — and the first three each carried a
 * byte-identical private copy of this pair. Activity became the fourth
 * caller when YAR-383 gave its detail modal a working pencil, so the pair
 * moved here instead of being duplicated a fourth time.
 */
export function taskToReviewItem(task: TaskRow): ReviewItem {
  const names = getChildrenFromTask(task).map((c) => c.name);
  return {
    type: 'task',
    title: task.title,
    child_name: names[0] ?? null,
    child_names: names,
    description: task.notes,
    start_datetime: null,
    end_datetime: task.end_date,
    due_datetime: task.due_date,
    location: task.location,
    link: task.link,
    links: task.links ?? null,
    related_event_id: task.related_event_id ?? null,
    is_time_unknown: !task.due_date || task.due_date.length <= 10,
  } as unknown as ReviewItem;
}

/** Event counterpart of {@link taskToReviewItem}. */
export function eventToReviewItem(event: EventRow): ReviewItem {
  const names = getChildrenFromEvent(event).map((c) => c.name);
  return {
    type: 'event',
    title: event.title,
    child_name: names[0] ?? null,
    child_names: names,
    description: event.notes,
    start_datetime: event.start_date,
    end_datetime: event.end_date,
    due_datetime: null,
    location: event.location,
    link: event.link,
    links: event.links ?? null,
    related_event_id: null,
    // NOTE: dead as written — `events.start_date` is a `timestamptz`, so
    // Postgres always returns a full offset-bearing string and this is never
    // true. The real flag now rides in on `source_review_item.is_time_unknown`
    // (see EVENT_SELECT). Left alone deliberately: this feeds ItemFormModal's
    // edit defaults, and flipping it would change which fields open enabled —
    // a form behaviour change that belongs to YAR-376 / YAR-378, not to this
    // rendering fix.
    is_time_unknown: event.start_date.length <= 10,
  } as unknown as ReviewItem;
}
